import React, { Fragment, ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from 'components/atoms/translated_text';
import { Translations } from './configuration';
import {
  AppDatePicker,
  AppTextField,
} from 'components/molecules/form_controls';
import GridLayout from 'components/molecules/grid_layout';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import ReimburseActions from 'redux/reducers/reimbursements_mscon_list_reducer/actions';
import * as DateUtils from 'utils/dates';
import * as CurrencyUtils from 'utils/currency';
import useQuery from 'custom_hooks/useQuery';

interface IProps {
  reimburse_info_mscon_id: number;
  iban: string;
  bic: string;
  vat_amount_to_be_reimbursed: number;
  reimbursement_date: Date;
}

interface FormFields {
  iban: string;
  bic: string;
  vat_amount_to_be_reimbursed: string;
  reimbursement_date: Date | null;
}

export default function EditReimburseInfoForm(props: IProps): ReactElement {
  // console.log('Render EditReimburseInfoForm');
  const dispatch = useDispatch();
  const defaultValues = {
    iban: props.iban ?? null,
    bic: props.bic ?? null,
    vat_amount_to_be_reimbursed:
      CurrencyUtils.formatCurrency(props.vat_amount_to_be_reimbursed) ?? '0.00',
    reimbursement_date: props.reimbursement_date
      ? new Date(props.reimbursement_date)
      : null,
  };

  const query = useQuery();
  const vat_num = query.get('vat_num');
  const schemeType = query.get('schemeType');
  const isPendingStr = query.get('isPending');
  const isPending = isPendingStr && isPendingStr === 'true' ? true : false;
  let searchParams = {
    ...(vat_num && { vat_num }),
    ...(schemeType && { schemeType }),
    ...(isPending && { isPending }),
  };

  const { errors, control, handleSubmit, watch } = useForm<FormFields>({
    defaultValues,
  });
  const classes = useStyles();

  const onSubmit = (data: any) => {
    dispatch(
      ReimburseActions.updateReimburseInfoMsconAction({
        reimburse_info_mscon_id: props.reimburse_info_mscon_id,
        iban: data.iban,
        bic: data.bic,
        vat_amount_to_be_reimbursed: props.vat_amount_to_be_reimbursed,
        reimbursement_date: DateUtils.convertToUtc(data.reimbursement_date),
        searchParams: searchParams,
      })
    );
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      <GridLayout
        elements={[
          {
            element: renderReimburseAmountField(errors, control),
            size: 12,
            id: 'vat_amount_to_be_reimbursed',
          },
          {
            element: renderReimburseDateField(
              errors,
              control,
              watch('reimbursement_date')
            ),
            size: 12,
            id: 'reimbursement_date',
          },
          {
            element: renderIbanField(errors, control, watch('iban')),
            size: 12,
            id: 'iban',
          },
          {
            element: renderBicField(errors, control),
            size: 12,
            id: 'bic',
          },
          {
            element: (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ width: '100%' }}
              >
                <TranslatedText textMap={Translations.submit} />
              </Button>
            ),
            size: 12,
            id: 'action',
          },
        ]}
      />
    </form>
  );
}

const renderReimburseAmountField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <Fragment>
      <AppTextField
        variant="outlined"
        margin="normal"
        fullWidth
        id="vat_amount_to_be_reimbursed"
        label={
          <TranslatedText
            textMap={Translations.vat_amount_to_be_reimbursed_control}
          />
        }
        error={'vat_amount_to_be_reimbursed' in errors}
        name="vat_amount_to_be_reimbursed"
        control={control}
        rules={
          {
            // required: 'Required Field',
          }
        }
        disabled={true}
        helperText={
          errors.vat_amount_to_be_reimbursed &&
          errors.vat_amount_to_be_reimbursed.message
        }
      />
    </Fragment>
  );
};

const renderIbanField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  watchIban: string | null
) => {
  /**
   * Returns true if invalid
   * @param value
   */
  function isInvalidIban(value: string | null) {
    const validation = new RegExp(/^[A-Z]{2}[0-9]{2}[0-9A-Z]{10,30}$/);
    let isValidIBAN = false;
    if (value != null && value.length !== 0) {
      isValidIBAN = !validation.test(value);
    }
    return isValidIBAN;
  }

  /**
   * Returns true if invalid
   * @param value
   */
  function isInvalidIbanCheckDigits(value: string | null) {
    var invalidIBANCheckDigits = false;

    if (value != null && value.length !== 0) {
      //clean-up IBAN from empty spaces
      var noWhiteSpaceIBAN: string = value.replace(/\s/g, '');

      //Step 1 - Move the first four characters at the end of the string
      var processedIBAN =
        noWhiteSpaceIBAN.substring(4, noWhiteSpaceIBAN.length) +
        noWhiteSpaceIBAN.substring(0, 4);

      //Step 2 - Replace the letters in IBAN with their corresponding digits, expanding the string since A=10, B=11 ... and Z=35.
      const asciiShift: number = 55;

      var checkSumString: string = '';

      Array.from(processedIBAN).forEach((character: string) => {
        var isLetter = /^[A-Z]$/i.test(character);

        checkSumString += isLetter
          ? character.charCodeAt(0) - asciiShift
          : character;
      });

      var checkSumDigit: number = Number(checkSumString.substring(0, 1));

      for (var i = 1; i < checkSumString.length; i++) {
        var v: number = Number(checkSumString.substring(i, i + 1));

        checkSumDigit *= 10;
        checkSumDigit += v;
        checkSumDigit %= 97;
      }

      invalidIBANCheckDigits = checkSumDigit !== 1;
    }
    return invalidIBANCheckDigits;
  }

  return (
    <Fragment>
      <AppTextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        id="iban"
        label={<TranslatedText textMap={Translations.iban_control} />}
        error={'iban' in errors}
        name="iban"
        control={control}
        rules={{
          required: 'Required Field',
          validate: () =>
            isInvalidIban(watchIban) || isInvalidIbanCheckDigits(watchIban)
              ? 'Invalid IBAN format'
              : null,
        }}
        helperText={errors.iban && errors.iban.message}
      />
    </Fragment>
  );
};

const renderBicField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <Fragment>
      <AppTextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        id="bic"
        label={<TranslatedText textMap={Translations.bic_control} />}
        error={'bic' in errors}
        name="bic"
        control={control}
        rules={{
          required: 'Required Field',
          pattern: {
            value: /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3}){0,1}$/,
            message: 'Invalid BIC format',
          },
        }}
        helperText={errors.bic && errors.bic.message}
      />
    </Fragment>
  );
};

const renderReimburseDateField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  watchDate: Date | null
) => {
  return (
    <AppDatePicker
      fullWidth
      margin="normal"
      label={
        <TranslatedText textMap={Translations.reimbursement_date_control} />
      }
      error={'reimbursement_date' in errors}
      control={control}
      required
      rules={{
        required: 'Required Field',
        validate: () =>
          (watchDate && watchDate <= new Date()) ||
          'Reimbursement date cannot be in the future!',
      }}
      autoOk
      name="reimbursement_date"
      helperText={
        errors.reimbursement_date && errors.reimbursement_date.message
      }
    />
  );
};
